import React, { useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Braces,
  ChevronRight,
  Loader2,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Modal from '../../components/Modal';
import { Skeleton } from '../../components/Skeleton';
import { formatDate, formatDateTime, relativeTime } from '../contracts/contractDetailUtils';
import StructuredValues from './StructuredValues';

// One card per schema on the contract's Structured Data tab. Covers the full
// state matrix: not-run / running (first + rerun) / failed (with and without
// last-good values) / complete / stale / orphaned. Phase b adds per-field
// edit-in-place (human edits lock against rerun overwrite), Mark verified,
// and a rerun-confirm modal when hand-edited fields exist.
//
// doc-112: the header is a disclosure button — sections collapse to a
// scannable row (name · chips · summary · recency · Run) and expand for the
// drill/verify moves. Mark verified only renders while expanded (attesting
// requires eyes on the values); a section auto-opens when its run starts or
// its action errors.

// Why an output is orphaned, keyed by the backend's orphanReason (doc-299).
// The old single sentence always blamed the contract TYPE, so a draft whose
// schema targets every type read as "no longer applies to this contract type"
// — the reader checks the type, finds it matches, and concludes the app is
// broken. An unknown or absent code falls back to the vague sentence rather
// than rendering nothing (a payload from before this shipped has no code).
const ORPHAN_REASONS = {
  archived: 'This schema is archived, so it no longer runs on any contract.',
  not_targeted: 'This schema no longer targets this contract type or category.',
  status_excluded: 'This schema skips contracts in this status (draft or terminated).',
};
const ORPHAN_REASON_FALLBACK = 'This schema is archived or no longer targets this contract.';

// Collapsed-only summary: what's in this section without opening it.
// filled = fields whose value is non-null/non-empty (arrays: length > 0);
// rows = total rows across array-of-objects fields.
export function summarizeOutput(fields, values) {
  const total = fields.length;
  let filled = 0;
  let rows = 0;
  for (const field of fields) {
    const v = values?.[field.key];
    if (v === null || v === undefined || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      if (v.every((item) => typeof item === 'object' && item !== null && !Array.isArray(item))) {
        rows += v.length;
      }
    }
    filled += 1;
  }
  const fieldsPart = `${filled} of ${total} field${total === 1 ? '' : 's'} extracted`;
  return rows > 0 ? `${rows} row${rows === 1 ? '' : 's'} · ${fieldsPart}` : fieldsPart;
}

function sourceLine(src) {
  if (src.kind === 'reference') return `Reference document — ${src.fileName || 'unknown'}`;
  if (src.kind === 'related') return `Related contract: ${src.contractTitle || src.contractId || 'unknown'} — ${src.fileName || 'unknown'}`;
  return `Contract document — ${src.fileName || 'unknown'}`;
}

function RunDetails({ output }) {
  const [open, setOpen] = useState(false);
  const run = output.run || {};
  const meta = [
    ['Model', run.model || '—'],
    ['Schema version', `v${output.schemaVersion}${output.schemaVersion < output.currentSchemaVersion ? ` (current v${output.currentSchemaVersion})` : ''}`],
    ['Tokens', run.inputTokens != null ? `${(run.inputTokens || 0).toLocaleString()} in · ${(run.outputTokens || 0).toLocaleString()} out` : '—'],
    ['Duration', run.durationMs != null ? `${(run.durationMs / 1000).toFixed(1)}s` : '—'],
    ['Run by', run.byName || '—'],
    ['Run at', formatDateTime(run.at)],
  ];
  return (
    <div style={{ borderTop: '0.5px solid var(--border-primary)' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px',
          background: 'none', border: 0, cursor: 'pointer', fontFamily: 'inherit',
          fontSize: 12.5, color: 'var(--text-secondary)',
        }}
      >
        <ChevronRight className="w-[12px] h-[12px]" style={{ opacity: 0.5, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 160ms' }} />
        Run details
        <span className="muted mono" style={{ fontSize: 11, marginLeft: 'auto' }}>
          {run.model} · {relativeTime(run.at)}
        </span>
      </button>
      <div style={{ display: 'grid', gridTemplateRows: open ? '1fr' : '0fr', transition: 'grid-template-rows 220ms var(--motion-ease)' }}>
        <div style={{ overflow: 'hidden' }}>
          <div className="stack-sm" style={{ padding: '4px 18px 16px' }}>
            <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'minmax(110px, max-content) 1fr', gap: '6px 24px', fontSize: 12 }}>
              {meta.map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt className="tertiary">{k}</dt>
                  <dd style={{ margin: 0 }}>{v}</dd>
                </React.Fragment>
              ))}
            </dl>
            {run.sources?.length > 0 && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.7, color: 'var(--text-tertiary)', marginBottom: 4 }}>
                  Sources read ({run.sources.length})
                </div>
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
                  {run.sources.map((src, i) => <li key={i}>{sourceLine(src)}</li>)}
                </ul>
              </div>
            )}
            {run.warnings?.length > 0 && (
              <div className="callout yellow">
                <div className="callout-icon"><AlertTriangle className="w-4 h-4" /></div>
                <div className="callout-body" style={{ fontSize: 12 }}>
                  <strong>The response didn't fully match the declared shape:</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                    {run.warnings.map((w, i) => <li key={i} className="mono" style={{ fontSize: 11.5 }}>{w}</li>)}
                  </ul>
                </div>
              </div>
            )}
            {run.prompt && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.7, color: 'var(--text-tertiary)', marginBottom: 4 }}>
                  Exact prompt
                </div>
                <pre className="mono" style={{
                  margin: 0, fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                  background: 'var(--surface-hover)', borderRadius: 'var(--corner-radius-sm)',
                  padding: 12, maxHeight: 320, overflow: 'auto',
                }}>{run.prompt}</pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StaleBanner({ output, onRerun, canWrite, running }) {
  const schemaStale = output.stale?.schema;
  return (
    <div className="callout yellow" style={{ marginBottom: 16 }}>
      <div className="callout-icon"><AlertTriangle className="w-4 h-4" /></div>
      <div className="callout-body" style={{ fontSize: 12.5 }}>
        {output.stale?.file ? (
          <><strong>The contract file changed</strong> after this was produced. Values below may be out of date.</>
        ) : (
          <><strong>Produced with schema v{output.schemaVersion}; current is v{output.currentSchemaVersion}.</strong> Rerun to use the latest definition.</>
        )}
        {schemaStale && output.stale?.file ? ' The schema definition also changed.' : ''}
      </div>
      {canWrite && (
        <button type="button" className="btn outline xs" onClick={onRerun} disabled={running}>
          Rerun
        </button>
      )}
    </div>
  );
}

export default function StructuredDataCard({ schema, output, canWrite, canRun = canWrite, onRun, onEditField, onRowAdd, onRowUpdate, onRowDelete, onVerify, running, sourceInfo = null, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rerunChoice, setRerunChoice] = useState('keep');
  const [includeRelated, setIncludeRelated] = useState(false);
  const [actionError, setActionError] = useState(null);
  const isStale = output?.status === 'complete' && (output.stale?.schema || output.stale?.file);
  const isPending = output?.status === 'pending';
  const isFailed = output?.status === 'failed';
  const hasValues = output && output.values && Object.keys(output.values).length > 0;
  const orphaned = output?.orphaned;
  const humanCount = output
    ? Object.values(output.provenance || {}).filter((p) => p?.source === 'human').length
    : 0;
  const relatedAvailable = sourceInfo?.relatedContracts || [];
  const referenceCount = sourceInfo?.referenceFiles?.length || 0;

  const startRun = (opts) => {
    setActionError(null);
    setOpen(true); // the skeleton/results (or the error) must be visible
    Promise.resolve(onRun(opts)).catch((err) => setActionError(err?.message || 'Run failed'));
  };

  // Every run entry point (header button, stale banner, failed retry) goes
  // through here. The confirm modal appears when there is a real choice to
  // make: hand-edited fields (keep vs overwrite) or related contracts whose
  // documents the run could include (doc-46).
  const needsRerunChoice = canWrite && output && !isPending && humanCount > 0;
  const handleRun = () => {
    if (needsRerunChoice || relatedAvailable.length > 0) {
      setRerunChoice('keep');
      setIncludeRelated(!!schema?.includeRelatedSources);
      setConfirmOpen(true);
    } else {
      startRun();
    }
  };

  const handleVerify = () => {
    setActionError(null);
    Promise.resolve(onVerify()).catch((err) => {
      setActionError(err?.message || 'Could not mark verified');
      setOpen(true);
    });
  };
  // Orphaned outputs may reference a schema the tab no longer receives — fall
  // back to rendering rows from the stored values so they stay readable.
  const fields = schema?.fields
    || output?.schemaFields
    || (hasValues ? Object.keys(output.values).map((key) => ({ key, label: key, type: 'string' })) : []);
  const name = schema?.name || output?.schemaName || 'Schema';
  const runLabel = output ? 'Rerun' : 'Run';
  const bodyId = `sd-sec-body-${schema?.id || output?.id || 'x'}`;
  // Collapsed-only summary line: values → rows/fields count; failed with
  // nothing to show → the error; pending first run → the chip carries it.
  const summary = hasValues
    ? summarizeOutput(fields, output.values)
    : (isFailed ? `Run failed — ${output?.error || 'Unknown error'}` : (output ? null : 'Not run yet'));

  return (
    <div className="card">
      <div className={`card-header compact${open ? '' : ' sd-sec-closed'}`}>
        <button
          type="button"
          className="sd-sec-toggle"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen(!open)}
        >
          <ChevronRight className="w-[14px] h-[14px] chev" />
          <Braces className="w-[13px] h-[13px]" style={{ color: 'var(--icon-secondary)', flexShrink: 0 }} />
          <span className="card-title">{name}</span>
          {output && output.status === 'complete' && (
            output.verification === 'verified'
              ? (
                <span
                  className="chip sq green"
                  title={`Verified by ${output.verifiedByName || 'unknown'}, ${formatDate(output.verifiedAt)}`}
                >
                  Verified
                </span>
              )
              : <span className="chip sq gray">Unreviewed</span>
          )}
          {isPending && (
            <span className="chip sq blue" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Loader2 className="w-3 h-3 animate-spin" /> Running…
            </span>
          )}
          {isFailed && <span className="chip sq red">Failed</span>}
          {isStale && <span className="chip sq yellow">Stale</span>}
          {orphaned && <span className="chip sq gray">Schema off</span>}
          {!open && summary && <span className="sd-sec-sum">{summary}</span>}
          {!open && output?.run?.at && <span className="sd-sec-meta">{relativeTime(output.run.at)}</span>}
        </button>
        {canRun && !orphaned && (
          <div className="row" style={{ gap: 6, flexShrink: 0 }}>
            {canWrite && open && output?.status === 'complete' && output.verification !== 'verified' && (
              <button
                type="button"
                className="btn ghost xs"
                onClick={handleVerify}
                disabled={isPending}
                title="Attest that the values below are correct"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                Mark verified
              </button>
            )}
            <button
              type="button"
              className="btn ghost xs"
              onClick={handleRun}
              disabled={running || isPending}
              title={`${runLabel} "${name}" against this contract's document`}
            >
              {(running || isPending) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {runLabel}
            </button>
          </div>
        )}
      </div>
      {open && (
        <div className="card-body flush" id={bodyId}>
          <div style={{ padding: '14px 18px' }}>
            {actionError && (
              <div style={{ color: 'var(--text-critical)', fontSize: 12, marginBottom: 10 }}>{actionError}</div>
            )}
            {isStale && <StaleBanner output={output} onRerun={handleRun} canWrite={canRun && !orphaned} running={running || isPending} />}

            {isFailed && (
              <div className="callout red" style={{ marginBottom: hasValues ? 16 : 0 }}>
                <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
                <div className="callout-body" style={{ fontSize: 12.5 }}>
                  Run failed: {output.error || 'Unknown error'}
                  {hasValues ? ' Showing the last successful values below.' : ''}
                </div>
                {canRun && !orphaned && (
                  <button type="button" className="btn outline xs" onClick={handleRun} disabled={running}>Retry</button>
                )}
              </div>
          )}

          {!output && (
            <div className="row" style={{ gap: 12 }}>
              <span className="muted" style={{ fontSize: 12.5 }}>Not run yet.</span>
              {canRun && (
                <button type="button" className="btn outline sm" onClick={handleRun} disabled={running}>
                  {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  Run {name}
                </button>
              )}
            </div>
          )}

          {isPending && !hasValues && (
            <div className="stack-sm">
              {['60%', '80%', '45%', '70%'].map((w) => (
                <div key={w} style={{ width: w }}><Skeleton /></div>
              ))}
            </div>
          )}

          {hasValues && (
            <div style={{ opacity: isPending ? 0.5 : 1, transition: 'opacity 160ms' }}>
              <StructuredValues
                fields={fields}
                values={output.values}
                provenance={output.provenance}
                canEdit={canWrite && !orphaned && !isPending && !!onEditField}
                onSaveField={(fieldKey, value) => onEditField(fieldKey, value)}
                rowOps={canWrite && !orphaned && !isPending && (onRowAdd || onRowUpdate || onRowDelete) ? {
                  onRowAdd, onRowUpdate, onRowDelete,
                } : null}
              />
            </div>
          )}
        </div>

        {orphaned && (
          <div className="muted" style={{ padding: '8px 18px 12px', fontSize: 12 }}>
            {ORPHAN_REASONS[output?.orphanReason] || ORPHAN_REASON_FALLBACK}
          </div>
        )}
        {output && output.run?.at && (output.status === 'complete' || (isFailed && hasValues)) && (
          <RunDetails output={output} />
        )}
      </div>
      )}

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={`${runLabel} ${name}?`}
        size="sm"
        icon={Sparkles}
        footer={(
          <>
            <button type="button" className="btn ghost" onClick={() => setConfirmOpen(false)}>Cancel</button>
            <button
              type="button"
              className="btn primary"
              onClick={() => {
                setConfirmOpen(false);
                startRun({
                  overwriteAll: needsRerunChoice && rerunChoice === 'overwrite',
                  ...(relatedAvailable.length > 0 ? { includeRelated } : {}),
                });
              }}
            >
              {runLabel}
            </button>
          </>
        )}
      >
        <div className="stack-sm" style={{ fontSize: 13 }}>
          {referenceCount > 0 && (
            <div className="muted" style={{ fontSize: 12.5 }}>
              Reads the contract document plus {referenceCount} reference document{referenceCount === 1 ? '' : 's'}.
            </div>
          )}
          {relatedAvailable.length > 0 && (
            <label className="row" style={{ gap: 8, cursor: 'pointer', alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={includeRelated}
                onChange={(e) => setIncludeRelated(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>
                Include documents from {relatedAvailable.length} related contract{relatedAvailable.length === 1 ? '' : 's'} (amendments, side letters)
                <span className="muted" style={{ display: 'block', fontSize: 11.5 }}>
                  {relatedAvailable.map((rc) => rc.title).join(' · ')}
                </span>
              </span>
            </label>
          )}
          {needsRerunChoice && (
            <>
              <div>{humanCount} field{humanCount === 1 ? ' was' : 's were'} edited by hand.</div>
              <label className="row" style={{ gap: 8, cursor: 'pointer', alignItems: 'flex-start' }}>
                <input
                  type="radio"
                  name={`rerun-mode-${output?.id || 'x'}`}
                  checked={rerunChoice === 'keep'}
                  onChange={() => setRerunChoice('keep')}
                  style={{ marginTop: 3 }}
                />
                <span>Keep hand-edited fields — the AI fills everything else (recommended)</span>
              </label>
              <label className="row" style={{ gap: 8, cursor: 'pointer', alignItems: 'flex-start' }}>
                <input
                  type="radio"
                  name={`rerun-mode-${output?.id || 'x'}`}
                  checked={rerunChoice === 'overwrite'}
                  onChange={() => setRerunChoice('overwrite')}
                  style={{ marginTop: 3 }}
                />
                <span>Overwrite everything, including hand edits</span>
              </label>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}

export function StructuredDataEmptyState({ isAdmin }) {
  return (
    <div className="card">
      <div className="card-body" style={{ padding: 40, textAlign: 'center' }}>
        <div className="muted" style={{ fontSize: 13 }}>No data schemas apply to this contract.</div>
        {isAdmin && (
          <Link to="/admin/schemas" className="btn ghost xs" style={{ marginTop: 10 }}>
            Manage data schemas →
          </Link>
        )}
      </div>
    </div>
  );
}
